import { Button } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { Calendar, ChevronDown } from "@hortis/ui/icons";
import { RichTextEditor } from "@hortis/ui/rich-text-editor";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { type ReactElement } from "react";
import { useFormContext } from "react-hook-form";
import { AccessionNumberInput } from "src/components/accession-number-input";
import {
  TaxonPicker,
  TaxonPickerLabel,
} from "src/components/autocomplete/scientific-names/taxon-picker";
import { MaterialReceivedPicker } from "src/components/pickers/material-received-picker";
import {
  ProvenancePicker,
  ProvenancePickerLabel,
} from "src/components/pickers/provenance-picker";
import {
  TagMultiPicker,
  TagMultiPickerLabel,
} from "src/components/pickers/tag-picker/tag-picker";
import { mention } from "src/components/rich-text-editor/mention";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import { twMerge } from "tailwind-merge";
import { FormTextField as TextField } from "../../../../components/text-field/text-field";
import { DonorPicker } from "./donor-picker";
import type { AccessionDataZod } from "./schema";

export const AccessionDataFields = (): ReactElement => {
  const form = useFormContext<Partial<AccessionDataZod>>();
  const accessToken = useAccessTokenBasic();
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ from: "/_layout/site/$siteSlug" });
  const tab = form.watch("accessionNumberFormat") ?? "AUTOMATIC";

  return (
    <>
      <div className="flex flex-col gap-8">
        <FormField
          control={form.control}
          name="taxonOption"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Taxon</FormLabel>
              <FormControl>
                <TaxonPicker data-cy="scientific-name" {...field}>
                  <Button
                    className="min-h-[44px] w-full justify-between px-[14px]! py-2!"
                    endIcon={ChevronDown}
                  >
                    {field.value == null ? (
                      <span className="text-grey-500">Select taxon...</span>
                    ) : (
                      <TaxonPickerLabel value={field.value} />
                    )}
                  </Button>
                </TaxonPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="accessionDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Accession date</FormLabel>
              <FormControl>
                <DatePicker
                  value={field.value}
                  onChange={(date) => {
                    field.onChange(date);
                  }}
                  data-cy="accession-date-picker"
                >
                  <Button endIcon={Calendar} className="justify-between">
                    {field.value == null
                      ? "Select an accession date..."
                      : createDateLabel(field.value)}
                  </Button>
                </DatePicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <AccessionNumberInput tab={tab} showLabel />
        <FormField
          control={form.control}
          name="provenanceCategoryCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Provenance</FormLabel>
              <FormControl>
                <ProvenancePicker value={field.value} onChange={field.onChange}>
                  <Button
                    className={twMerge(
                      "min-h-[44px] justify-between truncate px-[14px]!",
                      field.value == null && "text-grey-500",
                    )}
                    endIcon={ChevronDown}
                  >
                    {field.value == null ? (
                      "Select a provenance..."
                    ) : (
                      <ProvenancePickerLabel value={field.value} />
                    )}
                  </Button>
                </ProvenancePicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="materialTypeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Material received</FormLabel>
              <FormControl>
                <MaterialReceivedPicker
                  value={field.value}
                  onChange={field.onChange}
                >
                  <Button
                    className={twMerge(
                      "min-h-[44px] justify-between truncate px-[14px]!",
                      field.value == null && "text-grey-500",
                    )}
                    endIcon={ChevronDown}
                  >
                    {field.value == null
                      ? "Select material received..."
                      : field.value.name}
                  </Button>
                </MaterialReceivedPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="donor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Donor / supplier</FormLabel>
              <FormControl>
                <DonorPicker newDonorOption {...field}>
                  <Button
                    className="min-h-[44px] justify-between truncate px-[14px]! py-2!"
                    endIcon={ChevronDown}
                  >
                    {field.value == null || field.value === "" ? (
                      <span className="text-grey-500">
                        Select a donor / supplier...
                      </span>
                    ) : (
                      <div className="truncate text-grey-700">
                        {field.value}
                      </div>
                    )}
                  </Button>
                </DonorPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <TextField
          fullWidth
          name="ipenNumber"
          id="ipen-number"
          label="IPEN number"
          testId="ipen-number"
        />
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <FormControl>
                <TagMultiPicker
                  data-cy="accession-tags"
                  placeholder="Add tags..."
                  tagLevel="site"
                  newTagOption
                  {...field}
                >
                  <Button
                    className="min-h-[44px] justify-between py-2! pl-3.5!"
                    endIcon={ChevronDown}
                  >
                    {field.value == null || field.value.length === 0 ? (
                      <span className="text-grey-500">Add tags...</span>
                    ) : (
                      <TagMultiPickerLabel value={field.value} />
                    )}
                  </Button>
                </TagMultiPicker>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                {accessToken != null && (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Add notes..."
                    data-cy="accession-notes-input"
                    extensions={[mention({ accessToken, siteSlug, subdomain })]}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};
